import { agelForm } from 'agel-form'
import {
  getQueryFieldGroups,
  getSlotLabel,
  isQueryFieldRequired,
  orderQueryFields
} from '@/utils/queryFieldSettings'

// 未开启时沿用 agel-form；开启后只接管筛选项的显示和顺序，不改写查询模型。
export default {
  extends: agelForm,
  inject: {
    queryFieldSettingsContainer: { default: null }
  },
  props: {
    querySettings: { type: Boolean, default: false }
  },
  data() {
    return {
      queryFieldSettingsHidden: [],
      queryFieldSettingsOrder: [],
      queryFieldSettingsSlotLabels: {}
    }
  },
  computed: {
    $_queryFieldSettingsGroups() {
      return getQueryFieldGroups(
        Array.isArray(this.value.items) ? this.value.items : []
      )
    },
    $_queryFieldSettingsFields() {
      return this.$_queryFieldSettingsGroups
        .filter(
          ({ items: [{ prop, label, display, slot }] }) =>
            prop &&
            label !== '' &&
            display !== false &&
            (slot !== true || this.$scopedSlots[prop])
        )
        .map(({ items }) => {
          const { prop, label, slots } = items[0]
          return {
            prop,
            label:
              label ||
              this.queryFieldSettingsSlotLabels[prop] ||
              (typeof slots === 'string' ? slots : '') ||
              prop,
            // 组合条件里任一输入必填，整组都不可隐藏，但仍允许排序。
            required: items.some((item) =>
              isQueryFieldRequired(item, this.value.data, this.value.rules)
            )
          }
        })
    },
    $_queryFieldSettingsCacheKey() {
      const userId = this.$store?.getters?.id || 'anonymous'
      return this.$route?.name
        ? `query-field-settings:${userId}:${this.$route.name}`
        : ''
    },
    $_queryFieldSettingsItems() {
      if (!Array.isArray(this.value.items)) return this.value.items

      const fields = new Set(
        this.$_queryFieldSettingsFields.map(({ prop }) => prop)
      )
      // 再次过滤必填项，防止历史缓存或动态必填变化绕过面板的禁用状态。
      const hidden = new Set(
        this.$_queryFieldSettingsNormalizeHidden(this.queryFieldSettingsHidden)
      )
      const orderedGroups = orderQueryFields(
        this.$_queryFieldSettingsGroups.filter(({ prop }) => fields.has(prop)),
        this.queryFieldSettingsOrder
      )
      let index = 0

      return this.$_queryFieldSettingsGroups.flatMap((group) => {
        const current = fields.has(group.prop) ? orderedGroups[index++] : group
        return current.items.map((item) =>
          hidden.has(current.prop) ? { ...item, display: false } : item
        )
      })
    }
  },
  mounted() {
    if (!this.querySettings) return

    const labels = {}
    for (const prop of Object.keys(this.$scopedSlots)) {
      try {
        labels[prop] = getSlotLabel(this.$scopedSlots[prop]())
      } catch (error) {
        // shortcut: 需要作用域参数的动态插槽使用 prop，名称不清楚时补充 label。
      }
    }
    this.queryFieldSettingsSlotLabels = labels
    this.$_queryFieldSettingsLoad()
    if (this.queryFieldSettingsContainer) {
      this.queryFieldSettingsContainer.queryFieldSettingsForm = this
    }
    this.$_queryFieldSettingsRefreshLayout()
  },
  beforeDestroy() {
    if (this.queryFieldSettingsContainer?.queryFieldSettingsForm === this) {
      this.queryFieldSettingsContainer.queryFieldSettingsForm = null
    }
  },
  methods: {
    getItem(prop, deep) {
      // 字段联动继续修改原配置，避免写入用于显隐的临时副本。
      if (this.querySettings && !deep && Array.isArray(this.value.items)) {
        return this.value.items.find((item) => item.prop === prop)
      }
      return agelForm.methods.getItem.call(this, prop, deep)
    },
    $_queryFieldSettingsLoad() {
      const key = this.$_queryFieldSettingsCacheKey
      if (!key) return

      try {
        const cache = JSON.parse(localStorage.getItem(key))
        if (![1, 2].includes(cache?.version) || !Array.isArray(cache.hidden)) {
          return
        }
        // 即使缓存里曾隐藏必填项，本次进入也必须恢复显示。
        this.queryFieldSettingsHidden =
          this.$_queryFieldSettingsNormalizeHidden(cache.hidden)
        this.queryFieldSettingsOrder = orderQueryFields(
          this.$_queryFieldSettingsFields,
          cache.version === 2 && Array.isArray(cache.order) ? cache.order : []
        ).map(({ prop }) => prop)
      } catch (error) {
        // 缓存无效或被浏览器禁用时，使用原配置。
      }
    },
    // 缓存和面板共用显隐过滤，避免绕过必填保护。
    $_queryFieldSettingsNormalizeHidden(hiddenFields) {
      const fields = new Set(
        this.$_queryFieldSettingsFields
          .filter(({ required }) => !required)
          .map(({ prop }) => prop)
      )
      return [...new Set(hiddenFields.filter((prop) => fields.has(prop)))]
    },
    // 确认前只比较有效显隐，不提前修改状态或缓存，纯排序无需重置。
    $_queryFieldSettingsVisibilityChanged(hiddenFields) {
      const hidden = this.$_queryFieldSettingsNormalizeHidden(hiddenFields)
      const previous = new Set(
        this.$_queryFieldSettingsNormalizeHidden(this.queryFieldSettingsHidden)
      )
      return (
        hidden.length !== previous.size ||
        hidden.some((prop) => !previous.has(prop))
      )
    },
    $_queryFieldSettingsChange(hiddenFields, order) {
      const visibilityChanged =
        this.$_queryFieldSettingsVisibilityChanged(hiddenFields)
      const hidden = this.$_queryFieldSettingsNormalizeHidden(hiddenFields)
      // 由调用方确认重置已完成后提交，纯排序不触发重置。
      this.queryFieldSettingsHidden = hidden
      this.queryFieldSettingsOrder = orderQueryFields(
        this.$_queryFieldSettingsFields,
        order
      ).map(({ prop }) => prop)

      try {
        const key = this.$_queryFieldSettingsCacheKey
        if (key) {
          localStorage.setItem(
            key,
            JSON.stringify({
              version: 2,
              hidden,
              order: this.queryFieldSettingsOrder
            })
          )
        }
      } catch (error) {
        // 保存失败仍保留本次显示设置。
      }
      this.$_queryFieldSettingsRefreshLayout()
      return visibilityChanged
    },
    $_queryFieldSettingsRefreshLayout() {
      this.$nextTick(() => {
        this.queryFieldSettingsContainer?.updateHeightEvent()
        this.queryFieldSettingsContainer?.doLayout({ showLoading: false })
      })
    }
  },
  render(h) {
    const vnode = agelForm.render.call(this, h)
    if (this.querySettings) {
      // 只替换交给布局组件的 items，保留表单模型、插槽及原有实例方法。
      const layout = vnode.componentOptions.children[0]
      layout.componentOptions.propsData.items = this.$_queryFieldSettingsItems
    }
    return vnode
  }
}
